import React, { useEffect, useRef, useState } from "react";
import EcnButton from "../components/StyledComponents/EcnButton";
import EcnButtonFilledV2 from "../components/StyledComponents/EcnButtonFilledV2";
import { Icon } from "@iconify/react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { getJobDetail } from "../redux/slices/jobSlices/getJobDetailSlice";
import {
  Dialog,
  DialogContent,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import QuestionStepV2 from "../components/Apply/QuestionStepV2";

import { postJobApply } from "../redux/slices/jobSlices/postJobApplySlice";
import { correctEmail2 } from "../helper/Validations/BasicValidations";

import Lottie from "react-lottie";
import SuccessLottie from "../assets/lotties/Success_Job.json";
import ParsingLottie from "../assets/lotties/resume_parsing.json";
import Loader from "../components/UI/Loader";
import NotFound from "./NotFound";
import { useDropzone } from "react-dropzone";
import { getStorageToken } from "../redux/slices/jobSlices/getStorageTokenSlice";
import { postUserResume } from "../redux/slices/jobSlices/postUserResumeSlice";
import EcnInput from "../components/StyledComponents/EcnInput";
import PhoneInput from "react-phone-input-2";
import { maskCountry } from "../helper/mask";
import WorkGroupV2 from "../components/Apply/Sub/WorkGroupV2";
import EducationGroupV2 from "../components/Apply/Sub/EducationGroupV2";
import EcnCheckbox from "../components/StyledComponents/EcnCheckbox";
import { enqueueSnackbar } from "notistack";
import { getCompanyDetail } from "../redux/slices/jobSlices/getCompanyDetailSlice";

const REDIRECT_URL = process.env.REACT_APP_AUTH_REDIRECT || "";
const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE || "";
const JOB_BASE = process.env.REACT_APP_JOB_CREATE_BASE || "";

const ADMIN_BASE =
  GATEWAY_BASE === "https://api.dev.ecndev.io"
    ? "https://admin.dev.ecndev.io"
    : "https://admin.onefinnet.com";

const getDefaultCountryCode = () => {
  let region = "IN";

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz?.includes("Asia/Kolkata")) region = "IN";
    else if (tz?.includes("Europe/London")) region = "GB";
    else if (tz?.includes("Europe/Paris")) region = "FR";
    else if (tz?.includes("Europe/Berlin")) region = "DE";
    else if (tz?.includes("America/Toronto")) region = "CA";
    else if (tz?.includes("America/New_York")) region = "US";
    else if (tz?.includes("Australia/Sydney")) region = "AU";
    else if (tz?.includes("Asia/Shanghai")) region = "CN";
    else if (tz?.includes("Asia/Tokyo")) region = "JP";
  } catch { }

  const dialCodeMap: Record<string, string> = {
    US: "+1",
    IN: "+91",
    GB: "+44",
    FR: "+33",
    DE: "+49",
    CA: "+1",
    AU: "+61",
    CN: "+86",
    JP: "+81",
  };

  return dialCodeMap[region] || "+91";
};

const Apply = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { jobId, companyName } = useParams<{
    jobId: string;
    companyName: string;
  }>();

  const { jobData, loading } = useAppSelector((state) => state.getJobDetails);
  const { userInfo } = useAppSelector((state) => state.userDetails);
  const { storageToken } = useAppSelector((state) => state.getStorageToken);
  const { resumeLink, loading: uploadResumeLoading } = useAppSelector(
    (state) => state.postUserResume
  );
  const { companyDetails } = useAppSelector((state) => state.getCompanyDetails);

  const [activeStep, setActiveStep] = useState<number>(1);
  const [fileName, setFileName] = useState<string>("");
  const [noWorkExp, setNoWorkExp] = useState(false);
  const [successView, setSuccessView] = useState(false);
  const [submitClick, setSubmitClick] = useState(false);
  const [mobileError, setMobileError] = useState(false);
  const [termsAccepted, setIsTermsAccepted] = useState(false);
  const [infoCorrectAccepted, setInfoCorrectAccepted] = useState(false);
  const [showPromo, setShowPromo] = useState(false);

  //refs to scroll if theres an error
  const basicDetailsRef = useRef<HTMLInputElement | null>(null);
  const workRef = useRef<HTMLDivElement | null>(null);
  const educationRef = useRef<HTMLDivElement | null>(null);
  const resumeRef = useRef<HTMLDivElement | null>(null);

  //resume that will be parsed for autofill
  const [resumeFile, setResumeFile] = useState<any>([]);
  const [uploadClicked, setUploadClicked] = useState(false);
  const [isAutofillResumeParsed, setIsAutofillResumeParsed] = useState(false);
  //final resume that will be send to backend
  const [finalResumeFile, setFinalResumeFile] = useState<any>([]);
  const [finalUploadClicked, setFinalUploadClicked] = useState(false);
  const [isFinalResumeParsed, setIsFinalResumeParsed] = useState(false);
  const [showAutofillCompleteDialog, setShowAutofillCompleteDialog] =
    useState(false);
  //for error states below input fields
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [questionSubmitAttempted, setQuestionSubmitAttempted] = useState(false);

  const [appData, setAppData]: any = useState({
    job_id: "",
    user_id: "",
    basic_details: { first_name: "", last_name: "", email: "", phone: "" },
    resume: "",
    academic_scores: { act: 0, gpa: 0, sat: 0, gmat: 0 },
    education: [],
    work_experiences: [],
    job_questions: [
      {
        name: "",
        type: 0,
        key: "",
        save: false,
        options: [],
        values: [],
        required: false,
      },
    ],
  });
  // styles
  const [color, setColor] = useState("#0034BB");
  const [radius, setRadius] = useState("14px");
  const [fontStyle, setFontStyle] = useState("Plus Jakarta Sans");
  const [buttonRadius, setButtonRadius] = useState("28");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showWaterMark, setShowWaterMark] = useState(true);

  useEffect(() => {
    dispatch(getJobDetail({ jobId: jobId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, jobId]);

  useEffect(() => {
    if (companyName) {
      dispatch(getCompanyDetail(companyName));
    }
  }, [companyName, dispatch]);

  useEffect(() => {
    if (companyDetails) {
      const computedRadius = companyDetails?.border_radius
        ? `${companyDetails?.border_radius}`
        : "14px";
      const computedColor = companyDetails?.color || "#0034BB";
      const computedFontStyle = companyDetails?.font_style || "Plus Jakarta Sans";
      const radiusVal = parseFloat(computedRadius);
      const computedButtonRadius = radiusVal < 12
        ? `${Math.floor(radiusVal / 2)}`
        : `${Math.floor(radiusVal * 2)}`;

      setShowWaterMark(companyDetails?.show_watermark ?? true);
      setRadius(computedRadius);
      setColor(computedColor);
      setFontStyle(computedFontStyle);
      setButtonRadius(computedButtonRadius);
    }
  }, [companyDetails]);

  useEffect(() => {
    if (jobData && jobData.job_questions)
      setAppData({
        ...appData,
        job_id: jobData._id,
        job_questions: jobData.job_questions,
        job_recommended_questions: jobData?.job_recommended_questions,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobData]);

  // useEffect(() => {
  //   if (userInfo) {
  //     setAppData({
  //       ...appData,
  //       resume: userInfo.resume,
  //       user_id: userInfo._id,
  //       basic_details: {
  //         ...appData.basic_details,
  //         first_name: userInfo.first_name,
  //         last_name: userInfo.last_name,
  //         email: userInfo.email,
  //         phone: userInfo.phone,
  //       },
  //       academic_scores: {
  //         sat: userInfo.sat,
  //         gpa: userInfo.gpa,
  //         gmat: userInfo.gmat,
  //         act: userInfo.act,
  //       },
  //       education: userInfo.education,
  //       work_experiences:
  //         !userInfo?.work_experiences || userInfo.work_experiences.length === 0
  //           ? appData?.work_experiences
  //           : userInfo.work_experiences,
  //     });
  //     const split = userInfo?.resume?.split("/");
  //     setFileName(split[split?.length - 1]);
  //   }
  // }, [userInfo]);

  useEffect(() => {
    if (appData?.resume === null && userInfo?.resume) {
      setAppData({
        ...appData,
        resume: userInfo.resume,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, appData]);

  // useEffect(() => {
  //   dispatch(getUniAndClub());
  // }, []);

  // useEffect(() => {
  //   if (applyResponse && submitClick) {
  //     setSuccessView(true);
  //     setSubmitClick(false);
  //   }
  // }, [applyResponse, submitClick]);

  const isStepValid = () => {
    switch (activeStep) {
      case 1: {
        const { first_name, last_name, email, phone } = appData.basic_details;
        const { resume } = appData;
        let check1 = true;
        const hasValidWorkExp = (noWorkExp || (appData?.work_experiences?.length > 0));
        if (appData?.work_experiences?.length > 0) {
          check1 = appData?.work_experiences?.every(
            (work: any) =>
              work?.company &&
              work?.title &&
              work?.start_date &&
              ((work?.end_date &&
                work?.end_date !== "2030-10-05T14:48:00.000Z") ||
                work?.currently_working)
          );
        }
        if (appData?.education?.length > 0) {
          check1 =
            check1 &&
            appData?.education?.every(
              (edu: any) =>
                edu?.degree &&
                edu?.department &&
                edu?.institute &&
                edu?.start_date &&
                ((edu?.end_date && edu?.end_date !== "2030-10-05T14:48:00.000Z") || edu?.currently_studying)
            );
        }
        return (
          first_name &&
          last_name &&
          email &&
          correctEmail2.test(email) &&
          phone &&
          !mobileError &&
          check1 &&
          hasValidWorkExp &&
          resume
        );
      }
      case 2: {
        let check = true;
        if (appData?.job_questions?.length > 0)
          check = appData?.job_questions?.every((question: any) =>
            question.required
              ? question.values?.length > 0 && question?.values?.[0] !== ""
              : true
          );
        if (appData?.job_recommended_questions?.length > 0)
          check =
            check &&
            appData?.job_recommended_questions?.every((question: any) =>
              question.required
                ? question.key === "Scores"
                  ? question.score_options?.some((opt: any) => opt.required ? opt.values?.[0] : true)
                  : question.values?.length > 0 && question?.values?.[0] !== ""
                : true
            );
        return check;
      }
      default:
        return true;
    }
  };

  //scroll to the top point where there is an error
  const scrollToFirstError = () => {
    const { first_name, last_name, email, phone } = appData.basic_details;

    const hasInvalidBasic =
      !first_name ||
      !last_name ||
      !email ||
      !correctEmail2.test(email) ||
      !phone ||
      mobileError;

    const hasInvalidWork = (
      (appData?.work_experiences?.length === 0 && !noWorkExp) ||
      (appData?.work_experiences?.length > 0 &&
      !appData?.work_experiences?.every(
        (work: any) =>
          work?.company &&
          work?.title &&
          work?.start_date &&
          (work?.end_date || work?.currently_working)
      ))
    );

    const hasInvalidResume = (
      !appData?.resume ||
      appData?.resume === "null" ||
      appData?.resume === "undefined"
    )

    const hasInvalidEdu =
      appData?.education?.length > 0 &&
      !appData.education.every(
        (edu: any) =>
          edu?.degree &&
          edu?.department &&
          edu?.institute &&
          edu?.start_date &&
          (edu?.end_date || edu?.currently_studying)
      );

    if (hasInvalidBasic && basicDetailsRef.current) {
      basicDetailsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (hasInvalidWork && workRef.current) {
      workRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (hasInvalidResume && resumeRef.current) {
      resumeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (hasInvalidEdu && educationRef.current) {
      educationRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const backHandler = () => {
    navigate(`/${companyName}/job/${jobId}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const nextHandler = () => {
    setActiveStep((prev) => {
      if (prev === 3) {
        if (appData.job_questions.length > 0) {
          return prev + 1;
        } else {
          return prev + 2;
        }
      } else {
        return prev + 1;
      }
    });
  };

  const submitHandler = async () => {
    setSubmitClick(true);
    try {
      const payload = {
        ...appData,
        job_id: jobId,
        work_experiences: appData.work_experiences.map((work: any) => {
          const updated = {
            ...work,
            end_date:
              work.currently_working && !work.end_date
                ? "2030-10-05T14:48:00.000Z"
                : work.end_date,
            currently_working: !!work.currently_working,
          };
          delete updated.current_job;
          return updated;
        }),
        education: appData.education.map((edu: any) => ({
          ...edu,
          end_date:
            edu.currently_studying && !edu.end_date
              ? "2030-10-05T14:48:00.000Z"
              : edu.end_date,
          currently_studying: !!edu.currently_studying,
        })),
      };
      await dispatch(postJobApply(payload)).unwrap();
      setSuccessView(true);
      setSubmitClick(false);
    } catch (error: any) {
      setSubmitClick(false);
      console.log("here", error);
      const errorMessage =
        error?.message || "Job application failed. Please try again.";
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };
  const nextStepHandler = () => {
    setActiveStep((prevStep: any) => prevStep + 1);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const applyWithOnefinnetHandler = () => {
    const currentUrl = window.location.href;
    const redirectUrl = `${REDIRECT_URL}?origin=${encodeURIComponent(
      currentUrl
    )}`;
    window.location.href = redirectUrl;
  };

  const popupCloseHandler = () => {
    setSuccessView(false);
    navigate(`/${companyName}`);
  };

  const backStepHandler = () => {
    setActiveStep((prev) => prev - 1);
  };
  useEffect(() => {
    // If storage token is expired in resumeUploadHandler then new action is dispatched to getStorageToken() and file is set
    // else file is undefined and storage token is not changed
    if (
      resumeFile &&
      storageToken &&
      uploadClicked &&
      storageToken.valid_till > Date.now() / 1000
    ) {
      dispatch(
        postUserResume({
          storageToken,
          fileName,
          file: resumeFile?.[0],
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageToken, resumeFile, uploadClicked]);
  useEffect(() => {
    if (
      finalResumeFile &&
      storageToken &&
      finalUploadClicked &&
      storageToken.valid_till > Date.now() / 1000
    ) {
      dispatch(
        postUserResume({
          storageToken,
          fileName:
            finalResumeFile?.[0]?.name?.replace(/\.[^/.]+$/, "") +
            "_" +
            Date.now() +
            ".pdf",
          file: finalResumeFile?.[0],
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageToken, finalUploadClicked, finalResumeFile]);

  const onDrop = (acceptedFiles: File[]) => {
    setIsAutofillResumeParsed(false);
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    const file = acceptedFiles?.[0];
    if (!file) return;

    if (file.size > MAX_SIZE) {
      // setFileUploadFailed(true);
      return;
    }

    // setFileUploadFailed(false);

    setFileName(acceptedFiles?.[0].name);
    setResumeFile([
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      }),
    ]);

    setUploadClicked(true);
    if (!storageToken || Date.now() / 1000 > storageToken.valid_till) {
      dispatch(getStorageToken());
    } else {
      dispatch(
        postUserResume({
          storageToken,
          fileName:
            file.name.replace(/\.[^/.]+$/, "") + "_" + Date.now() + ".pdf",
          file: file,
        })
      );
    }
  };

  const onResumeDrop = (acceptedFiles: File[]) => {
    setIsFinalResumeParsed(false);
    setFinalUploadClicked(true);
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    const file = acceptedFiles?.[0];
    if (!file) return;

    if (file.size > MAX_SIZE) {
      // setFileUploadFailed(true);
      return;
    }

    // setFileUploadFailed(false);

    // setFileName(acceptedFiles?.[0].name);
    setFinalResumeFile([
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      }),
    ]);
    if (!storageToken || Date.now() / 1000 > storageToken.valid_till) {
      dispatch(getStorageToken());
    } else {
      dispatch(
        postUserResume({
          storageToken,
          fileName:
            file.name.replace(/\.[^/.]+$/, "") + "_" + Date.now() + ".pdf",
          file: file,
        })
      );
    }
  };

  useEffect(() => {
    if (resumeLink && resumeFile.length > 0 && uploadClicked) {
      const autofillDetails = async () => {
        try {
          const response = await fetch(
            `${GATEWAY_BASE + JOB_BASE}/api/v1/aiFeatures/extractResumeDetails`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ resume_url: resumeLink }),
              credentials: "include",
            }
          );
          if (!response.ok) {
            throw new Error(`API error: ${response.statusText}`);
          }

          const data = await response.json();
          setIsAutofillResumeParsed(true);
          setShowAutofillCompleteDialog(true);
          setFinalResumeFile(resumeFile);
          setIsFinalResumeParsed(true);
          setAppData((prevAppData: any) => ({
            ...prevAppData,
            basic_details: {
              ...prevAppData?.basic_details,
              first_name: data?.first_name,
              last_name: data?.last_name,
              email: data?.email,
              phone:
                data?.country_code && data?.mobile
                  ? data?.country_code + data?.mobile
                  : getDefaultCountryCode() + (data?.mobile ?? ""),
              resume: resumeLink,
            },
            resume: resumeLink,
            work_experiences: data?.work_experience?.map((wk: any) => ({
              ...wk,
              currently_working: wk.current_job,
            })) || [],
            education: data?.education,
          }));
          setUploadClicked(false);
        } catch {
          setUploadClicked(false);
        }
      };
      autofillDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeLink, resumeFile, uploadClicked]);

  useEffect(() => {
    if (
      resumeLink &&
      !uploadResumeLoading &&
      finalResumeFile.length > 0 &&
      finalUploadClicked
    ) {
      const autofillDetails = async () => {
        try {
          setIsFinalResumeParsed(true);
          setFinalUploadClicked(false);
          setAppData({ ...appData, resume: resumeLink });
        } catch { }
      };
      autofillDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeLink, finalResumeFile, finalUploadClicked]);

  const {
    getRootProps,
    getInputProps,
  } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      "application/pdf": [".pdf"]
    },
  });
  const {
    getRootProps: getResumeRootProps,
    getInputProps: getResumeInputProps,
  } = useDropzone({
    onDrop: onResumeDrop,
    maxFiles: 1,
    accept: {
      "application/pdf": [".pdf"]
    },
  });
  const basicDetailsChangeHandler = (e: any) => {
    setAppData({
      ...appData,
      basic_details: {
        ...appData.basic_details,
        [e.target.name]: e.target.value,
      },
    });
  };

  const mobileChangeHandler = (
    val: any,
    country: any,
    e: any,
    formatVal: any
  ) => {
    if (formatVal.length !== country?.format.length) {
      setMobileError(true);
    } else {
      setMobileError(false);
    }

    setAppData({
      ...appData,
      basic_details: {
        ...appData.basic_details,
        phone: formatVal,
      },
    });
  };
  const noWorkExpHandler = (e: any) => {
    setNoWorkExp(e.target.checked);
    if (e.target.checked === true) {
      setAppData({ ...appData, work_experiences: [] });
    } else {
      setAppData({ ...appData, work_experiences: [] });
    }
  };

  const updateWorkExperience = (index: number, updatedWork: any) => {
    const updatedWorkExperiences = appData.work_experiences.map(
      (work: any, i: number) =>
        i === index ? { ...work, ...updatedWork } : work
    );
    setAppData({ ...appData, work_experiences: updatedWorkExperiences });
  };

  const removeWorkExperience = (index: number) => {
    const updatedWorkExperiences = appData.work_experiences.filter(
      (work: any, i: number) => i !== index
    );
    setAppData({ ...appData, work_experiences: updatedWorkExperiences });
  };

  const changeHandler = (index: number, field: string, value: any) => {
    // Check if the field is either "education_type" or "graduation_year" and convert the value to a number
    const newValue =
      field === "graduation_year"
        ? Number(value)
        : field === "education_type"
          ? value === "Undergrad"
            ? 0
            : 1
          : value;

    // Update the education array with the new value
    const newEducation = appData.education.map((edu: any, i: number) =>
      i === index ? { ...edu, [field]: newValue } : edu
    );

    // Set the updated education array in the appData state
    setAppData({ ...appData, education: newEducation });
  };

  const removeEducationHandler = (index: number) => {
    const newEducation = appData.education.filter(
      (_: any, i: number) => i !== index
    );
    setAppData({ ...appData, education: newEducation });
  };

  const addEducationHandler = () => {
    setAppData({
      ...appData,
      education: [...appData.education, { ...eduTemplate }],
    });
  };
  const clearInfoHandler = (clearInfoType: string) => {
    switch (clearInfoType) {
      case "personalInfo":
        setAppData((prevData: any) => ({
          ...prevData,
          basic_details: {
            ...prevData?.basic_details,
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
          },
        }));
        break;
      case "workInfo":
        setAppData((prevData: any) => ({
          ...prevData,
          work_experiences: [],
        }));
        break;
      case "educationInfo":
        setAppData((prevData: any) => ({
          ...prevData,
          education: [],
        }));
        break;
    }
  };

  const isSingleStep =
    !jobData?.job_questions?.length &&
    !jobData?.job_recommended_questions?.length;

  return (
    <>
      {loading ? (
        <div className="loader-container">
          <Loader color={color} />
        </div>
      ) : jobData ? (
        <div className="job-applyV2" style={{ fontFamily: fontStyle }}>
          <div className="heading">
            <EcnButton
              color="secondary"
              startIcon={<Icon icon="mingcute:left-line" />}
              onClick={() => {
                navigate(`/${companyName}/job/${jobData?._id}`);
              }}
            >
              {jobData?.job_title}
            </EcnButton>
          </div>
          <div className="body-container">
            <div className="left-container" style={{ borderRadius: radius }}>
              <div className="progress-line">
                <div
                  style={{
                    width: isSingleStep ? "58%" : "",
                    background: activeStep >= 1 ? color : "",
                  }}
                  className={`connector ${activeStep >= 1 ? "active" : ""}`}
                ></div>
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                    fill="white"
                    stroke={color}
                    strokeWidth="2"
                  />
                </svg>
                {!isSingleStep && (
                  <>
                    <div
                      style={{
                        background: activeStep >= 2 ? color : "",
                      }}
                      className={`connector ${activeStep >= 2 ? "active" : ""}`}
                    ></div>
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                      <circle
                        cx="8"
                        cy="8"
                        r="7"
                        fill="white"
                        stroke={color}
                        strokeWidth="2"
                      />
                    </svg>
                  </>
                )}
                <div
                  style={{ width: isSingleStep ? "58%" : "" }}
                  className={`connector ${activeStep >= 3 ? "active" : ""}`}
                ></div>
              </div>
              {activeStep === 1 && (
                <>
                  <div className="progress-topics">
                    <div className="topic">Candidate Details</div>
                    {!isSingleStep && (
                      <div className="topic">Recruiter Questions</div>
                    )}
                  </div>
                  <div className="resume-autofill-container">
                    <div className="title">
                      <div className="upload-resume">
                        <Icon
                          icon={"mingcute:flash-line"}
                          width={20}
                          height={20}
                          color={color}
                        />
                        <h1>Upload Resume for Rapid Autofill</h1>
                      </div>
                      <p>
                        Save your time by uploading your resume to autofill
                        information.
                      </p>
                    </div>
                    <div
                      className="resume-upload-container"
                      style={{ borderRadius: radius }}
                      {...getRootProps()}
                    >
                      <input {...getInputProps()} />
                      {!isAutofillResumeParsed ? (
                        <div className="mid-container">
                          <div className="apply-upload-icon">
                            <Icon
                              icon={"mingcute:file-upload-line"}
                              width={24}
                              height={24}
                              color={color}
                            />
                          </div>
                          <h2>
                            <span style={{ color: color }}>
                              Click to upload
                            </span>{" "}
                            or drag and drop
                          </h2>
                          <p>(Only .pdf)</p>
                        </div>
                      ) : (
                        <div className="mid-container">
                          <div
                            className="uploaded"
                            style={{ borderRadius: radius }}
                          >
                            <div className="file-name">
                              <Icon
                                icon={"mingcute:attachment-line"}
                                width={20}
                                height={20}
                                color="#667085"
                              />
                              {resumeFile?.[0]?.name}
                            </div>
                            <div className="file-name">
                              {" "}
                              <Icon
                                icon={"mingcute:eye-line"}
                                className="icon"
                                width={20}
                                height={20}
                                color="#667085"
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  window.open(`${resumeLink}`);
                                }}
                              />{" "}
                              <Icon
                                icon={"mingcute:close-line"}
                                width={20}
                                className="icon"
                                height={20}
                                color="#667085"
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  setResumeFile([]);
                                  setIsAutofillResumeParsed(false);
                                }}
                              />
                            </div>
                          </div>
                          <h2>
                            <span style={{ color: color }}>
                              Upload new file
                            </span>{" "}
                            or drag and drop here to replace
                          </h2>
                          <p>(Only .pdf)</p>
                        </div>
                      )}
                    </div>
                    {showAutofillCompleteDialog ? (
                      <div
                        className="autofill-complete-container"
                        style={{ borderRadius: radius }}
                      >
                        <h1>
                          Autofill completed! Please review the information we
                          have filled in below.
                        </h1>
                        <Icon
                          icon={"mingcute:close-line"}
                          width={20}
                          height={20}
                          color="##09244B"
                          className="icon"
                          onClick={(e: any) => {
                            e.stopPropagation();
                            setShowAutofillCompleteDialog(false);
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="personal-info-container">
                    <div className="header">
                      <h1>Personal Information</h1>
                      <div
                        className="icon-container"
                        style={{ color: color }}
                        onClick={() => clearInfoHandler("personalInfo")}
                      >
                        <Icon
                          icon={"mingcute:delete-2-line"}
                          width={20}
                          height={20}
                          color={color}
                        />
                        Clear
                      </div>
                    </div>
                    <div className="row" ref={basicDetailsRef}>
                      <div className="col">
                        <p>
                          First Name <span style={{ color: color }}>*</span>
                        </p>
                        <EcnInput
                          name="first_name"
                          className="ecn-input"
                          helperText={
                            submitAttempted &&
                              !appData.basic_details.first_name ? (
                              <p className="custom-error-text">
                                First name is required
                              </p>
                            ) : null
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: radius,

                              "& fieldset": {
                                borderColor: "#e4e7ec",
                              },
                              "&:hover fieldset": {
                                borderColor: color,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: color,
                              },
                              "& input": {
                                color: color,
                              },
                            },
                          }}
                          value={appData.basic_details.first_name}
                          onChange={basicDetailsChangeHandler}
                        />
                      </div>
                      <div className="col">
                        <p>
                          Last Name <span style={{ color: color }}>*</span>
                        </p>
                        <EcnInput
                          className="ecn-input"
                          helperText={
                            submitAttempted &&
                              !appData.basic_details.last_name ? (
                              <p className="custom-error-text">
                                Last name is required
                              </p>
                            ) : null
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: radius,

                              "& fieldset": {
                                borderColor: "#e4e7ec",
                              },
                              "&:hover fieldset": {
                                borderColor: color,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: color,
                              },
                              "& input": {
                                color: color,
                              },
                            },
                          }}
                          name="last_name"
                          value={appData.basic_details.last_name}
                          onChange={basicDetailsChangeHandler}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <p>
                          Email <span style={{ color: color }}>*</span>
                        </p>
                        <EcnInput
                          className="ecn-input"
                          helperText={
                            submitAttempted && (
                              appData?.basic_details?.email ? (
                                !correctEmail2.test(
                                  appData?.basic_details?.email
                                ) ? (
                                  <p className="custom-error-text">
                                    Valid email is required
                                  </p>
                                ) : null
                              ) : (
                                <p className="custom-error-text">
                                  Email is required
                                </p>
                              )
                            )
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: radius,

                              "& fieldset": {
                                borderColor: "#e4e7ec",
                              },
                              "&:hover fieldset": {
                                borderColor: color,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: color,
                              },
                              "& input": {
                                color: color,
                              },
                            },
                          }}
                          name="email"
                          value={appData?.basic_details?.email}
                          onChange={basicDetailsChangeHandler}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon
                                  icon="mdi:email-outline"
                                  width={20}
                                  height={20}
                                  color="#9FA8BB"
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </div>
                      <div className="row">
                        <div className="col">
                          <p>
                            Contact <span style={{ color: color }}>*</span>
                          </p>
                          <PhoneInput
                            country="us"
                            preferredCountries={[
                              "gb",
                              "fr",
                              "es",
                              "it",
                              "cn",
                              "in",
                              "us",
                              "ca",
                            ]}
                            containerStyle={{
                              borderRadius: radius,
                            }}
                            inputStyle={{
                              borderRadius: radius,
                            }}
                            buttonStyle={{
                              borderRadius: radius,
                            }}
                            containerClass="mobile-input"
                            specialLabel=""
                            placeholder="Type your mobile number"
                            masks={maskCountry}
                            isValid={(inputNumber, country, countries) => {
                              const valid = countries.some((country: any) => {
                                return inputNumber.startsWith(country.dialCode);
                              });
                              if (!valid) setMobileError(true);
                              return true;
                            }}
                            value={appData.basic_details.phone}
                            onChange={(val, country, e, formatVal) =>
                              mobileChangeHandler(val, country, e, formatVal)
                            }
                          />
                          {submitAttempted && mobileError ? (
                            <p className="custom-error-text">
                              Contact is required
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="personal-info-container" ref={workRef}>
                    <div className="header">
                      <h1>Work Experience</h1>
                      <div
                        className="icon-container"
                        style={{ color: color }}
                        onClick={() => clearInfoHandler("workInfo")}
                      >
                        <Icon
                          icon={"mingcute:delete-2-line"}
                          width={20}
                          height={20}
                          color={color}
                        />
                        Clear
                      </div>
                    </div>
                    <FormControlLabel
                      className="no-work-label"
                      control={
                        <EcnCheckbox
                          sx={{
                            color: color,
                            "& .MuiSvgIcon-root": {
                              borderRadius: radius, // dynamic border radius
                            },
                          }}
                          checked={noWorkExp}
                          onChange={noWorkExpHandler}
                          color="primary"
                        />
                      }
                      sx={{
                        ".MuiFormControlLabel-label": {
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#1D2939",
                        },
                      }}
                      label="I do not have relevant work experience"
                    />

                    <div className="work-containers">
                      {appData?.work_experiences?.map(
                        (work: any, index: number) => (
                          <WorkGroupV2
                            key={index}
                            work={work}
                            index={index}
                            noWorkExp={noWorkExp}
                            updateWorkExperience={updateWorkExperience}
                            removeWorkExperience={removeWorkExperience}
                            isLast={appData.work_experiences.length === 1}
                            submitAttempted={submitAttempted}
                            color={color}
                            radius={radius}
                          />
                        )
                      )}
                    </div>
                    <div
                      className={noWorkExp ? "add-button disabled" : "add-button"}
                      style={noWorkExp ? {
                        cursor: "not-allowed",
                        opacity: 0.5,
                      } : {
                        cursor: "pointer"
                      }}
                      onClick={
                        !noWorkExp
                          ? () => {
                            setAppData((prevAppData: any) => ({
                              ...prevAppData,
                              work_experiences: [
                                ...prevAppData.work_experiences,
                                workTemplate,
                              ],
                            }))
                          }
                          : undefined
                      }
                    >
                      <Icon
                        icon={"mingcute:add-line"}
                        width={20}
                        height={20}
                        color={color}
                      />
                      Add
                    </div>
                    {submitAttempted && (noWorkExp ? "" : appData?.work_experiences?.length === 0) ? (
                      <p className="custom-error-text">
                        At least one work experience is required or select "I do not have relevant work experience"
                      </p>
                    ) : null}
                  </div>
                  <div className="personal-info-container" ref={educationRef}>
                    <div className="header">
                      <h1>Education</h1>
                      <div
                        className="icon-container"
                        style={{ color: color }}
                        onClick={() => clearInfoHandler("educationInfo")}
                      >
                        <Icon
                          icon={"mingcute:delete-2-line"}
                          width={20}
                          height={20}
                          color={color}
                        />
                        Clear
                      </div>
                    </div>

                    <div className="work-containers">
                      {appData?.education?.map((edu: any, index: number) => (
                        <EducationGroupV2
                          submitAttempted={submitAttempted}
                          key={index}
                          edu={edu}
                          index={index}
                          onChange={changeHandler}
                          onRemove={removeEducationHandler}
                          isLast={appData.education.length === 1}
                          color={color}
                          radius={radius}
                        />
                      ))}
                    </div>
                    <div className="add-button" onClick={addEducationHandler}>
                      <Icon
                        icon={"mingcute:add-line"}
                        width={20}
                        height={20}
                        color={color}
                      />
                      Add
                    </div>
                  </div>
                  <div className="resume-container" ref={resumeRef}>
                    <div className="title">
                      <h1>Attachments</h1>
                    </div>
                    <div
                      className="resume-upload-container"
                      style={{ borderRadius: radius }}
                      {...getResumeRootProps()}
                    >
                      <input {...getResumeInputProps()} />
                      {!isFinalResumeParsed ? (
                        <div className="mid-container">
                          <div className="apply-upload-icon">
                            <Icon
                              icon={"mingcute:file-upload-line"}
                              width={24}
                              height={24}
                              color={color}
                            />
                          </div>

                          <h2>
                            <span style={{ color: color }}>
                              Click to upload
                            </span>{" "}
                            or drag and drop
                          </h2>
                          <p>(Only .pdf)</p>
                        </div>
                      ) : (
                        <div className="mid-container">
                          <div
                            className="uploaded"
                            style={{ borderRadius: radius }}
                          >
                            <div className="file-name">
                              <Icon
                                icon={"mingcute:attachment-line"}
                                width={20}
                                height={20}
                                color="#667085"
                              />
                              {finalResumeFile?.[0]?.name}
                            </div>
                            <div className="file-name">
                              {" "}
                              <Icon
                                icon={"mingcute:eye-line"}
                                width={20}
                                height={20}
                                className="icon"
                                color="#667085"
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  window.open(`${resumeLink}`);
                                }}
                              />{" "}
                              <Icon
                                icon={"mingcute:close-line"}
                                width={20}
                                height={20}
                                color="#667085"
                                style={{
                                  cursor: "pointer",
                                }}
                                className="icon"
                                onClick={(e: any) => {
                                  e.stopPropagation();
                                  setFinalResumeFile([]);
                                  setIsFinalResumeParsed(false);
                                  appData.resume = "";
                                }}
                              />
                            </div>
                          </div>
                          <h2>
                            <span style={{ color: color }}>
                              Upload new file
                            </span>{" "}
                            or drag and drop here to replace
                          </h2>
                          <p>(Only .pdf)</p>
                        </div>
                      )}
                    </div>
                    {submitAttempted && !appData?.resume ? (
                      <p className="custom-error-text">
                        Resume/Attachment is required
                      </p>
                    ) : null}
                  </div>
                  <div className="personal-info-container">
                    <div className="header">
                      <h1>Terms of Service</h1>
                    </div>
                    <div className="terms-of-service">
                      <div className="row">
                        <EcnCheckbox
                          sx={{ color: color }}
                          checked={termsAccepted}
                          onChange={(e: any) => {
                            setIsTermsAccepted(e.target.checked);
                          }}
                        />{" "}
                        <h2>
                          I hereby acknowledge and accept all{" "}
                          <span
                            style={{
                              color: color,
                              cursor: "pointer"
                            }}
                            onClick={() => {
                              window.open(
                                "https://onefinnet.com/legal",
                                "_blank"
                              );
                            }}
                          >
                            terms and conditions.
                          </span>
                        </h2>
                        {submitAttempted && !termsAccepted ? (
                          <p className="custom-error-text">
                            Accept Terms and Conditions
                          </p>
                        ) : null}
                      </div>
                      <div className="row">
                        <EcnCheckbox
                          sx={{ color: color }}
                          checked={infoCorrectAccepted}
                          onChange={(e: any) =>
                            setInfoCorrectAccepted(e.target.checked)
                          }
                        />
                        <h2>
                          I hereby confirm that all the information provided
                          above is accurate to the best of my knowledge.
                        </h2>
                        {submitAttempted && !infoCorrectAccepted ? (
                          <p className="custom-error-text">
                            Confirm Information Accuracy
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="submit-buttons">
                      <EcnButtonFilledV2
                        disabled={
                          // !(
                          //   termsAccepted &&
                          //   infoCorrectAccepted &&
                          //   isStepValid()
                          // ) || submitClick
                          submitClick
                        }
                        variant="contained"
                        className=""
                        onClick={() => {
                          setSubmitAttempted(true);
                          scrollToFirstError();
                          if (
                            isStepValid() &&
                            termsAccepted &&
                            infoCorrectAccepted
                          ) {
                            isSingleStep ? submitHandler() : nextStepHandler();
                          }
                        }}
                        sx={{ width: "200px", borderRadius: buttonRadius }}
                      >
                        {isSingleStep ? `Submit` : `Go to Next Step`}
                      </EcnButtonFilledV2>
                      <EcnButtonFilledV2
                        disabled={false}
                        variant="contained"
                        className="close-btn"
                        onClick={() => {
                          navigate(`/${companyName}/job/${jobData?._id}`);
                        }}
                        sx={{ width: "200px", borderRadius: buttonRadius }}
                      >
                        Close Application
                      </EcnButtonFilledV2>
                    </div>
                  </div>
                </>
              )}
              {activeStep === 2 && (
                <>
                  <div className="progress-topics">
                    <div className="topic">Candidate Details</div>
                    {!isSingleStep && (
                      <div className="topic">Recruiter Questions</div>
                    )}
                  </div>

                  <div className="personal-info-container">
                    <div className="header">
                      <h1>Recruiter Questions</h1>
                    </div>
                    <div>
                      <QuestionStepV2
                        appData={appData}
                        setAppData={setAppData}
                        submitAttempted={questionSubmitAttempted}
                        color={color}
                        radius={radius}
                      />
                    </div>
                    <div className="submit-buttons">
                      <EcnButtonFilledV2
                        // disabled={!isStepValid()}
                        disabled={
                          submitClick
                        }
                        variant="contained"
                        className=""
                        onClick={() => {
                          setQuestionSubmitAttempted(true);
                          scrollToFirstError();
                          if (
                            isStepValid()
                          ) {
                            submitHandler()
                          }
                        }}
                        sx={{ width: "200px", borderRadius: buttonRadius }}
                      >
                        Submit
                      </EcnButtonFilledV2>
                      <EcnButtonFilledV2
                        disabled={false}
                        variant="contained"
                        className="close-btn"
                        onClick={() => {
                          backStepHandler();
                        }}
                        sx={{ width: "200px", borderRadius: buttonRadius }}
                      >
                        Go to Previous Step
                      </EcnButtonFilledV2>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="right-container">
              <div className="company-details" style={{ borderRadius: radius }}>
                <div className="header">
                  <img src={jobData?.display_picture} alt=""></img>
                  <div className="content">
                    <h1>{jobData?.job_title}</h1>
                    <p
                      style={{
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        window.open(jobData?.website_link, "_blank");
                      }}
                    >
                      {jobData?.organisations?.[0]}
                      {"  "}
                      <Icon
                        icon={"mingcute:arrow-right-up-line"}
                        height={16}
                        width={16}
                        color="#9fa8bb"
                      />
                    </p>
                  </div>
                </div>
                <div className="items">
                  <div
                    className="container"
                    style={{ borderRadius: buttonRadius }}
                  >
                    <Icon
                      icon={"mingcute:necktie-line"}
                      height={16}
                      width={16}
                      color={color}
                    />
                    {jobData?.job_roles?.[0]}
                  </div>
                  <div
                    className="container"
                    style={{ borderRadius: buttonRadius }}
                  >
                    <Icon
                      icon={"mingcute:suitcase-line"}
                      height={16}
                      width={16}
                      color={color}
                    />
                    {jobData?.role_type}
                  </div>
                  <div
                    className="container"
                    style={{ borderRadius: buttonRadius }}
                  >
                    <Icon
                      icon={"mingcute:time-line"}
                      height={16}
                      width={16}
                      color={color}
                    />
                    {jobData?.max_experience_required !== 100
                      ? jobData?.experience_required +
                      " - " +
                      jobData?.max_experience_required
                      : jobData?.experience_required + "+"}{" "}
                    years of Experience
                  </div>
                  <div
                    className="container"
                    style={{ borderRadius: buttonRadius }}
                  >
                    <Icon
                      icon={"mingcute:building-2-line"}
                      height={16}
                      width={16}
                      color={color}
                    />
                    {jobData?.industries?.[0]}
                  </div>
                  <div
                    className="container"
                    style={{ borderRadius: buttonRadius }}
                  >
                    <Icon
                      icon={"mingcute:location-line"}
                      height={16}
                      width={16}
                      color={color}
                    />
                    {jobData?.address?.cities?.[0]}
                  </div>
                </div>
              </div>
              <div
                className="promo-dialog-container"
                style={{ borderRadius: radius }}
              >
                <span
                  style={{
                    marginBottom: "-10px",
                    color: "rgb(159, 168, 187)",
                    fontSize: "14px",
                  }}
                >Ad</span>
                <div>
                  <img
                    src="https://ecn.blob.core.windows.net/ecn/images/talent ad.png"
                    alt=""
                    className="ad-img"
                  />
                </div>
                <div className="text-container">
                  <h1>Post a Job today</h1>
                  <p>
                    With Onefinnet Talent, you can quickly post a job and find
                    the right candidates.
                  </p>
                </div>
                <EcnButtonFilledV2
                  className="featured-button"
                  sx={{ width: "200px", borderRadius: buttonRadius }}
                  onClick={() => {
                    window.open(
                      ADMIN_BASE + "/signup?client=third-party&cupoun=FINFIRST",
                      "_blank"
                    );
                  }}
                >
                  Post a Featured Job
                </EcnButtonFilledV2>
                <EcnButtonFilledV2
                  className="try-button"
                  sx={{ width: "200px", borderRadius: buttonRadius }}
                  onClick={() => {
                    window.open("https://admin.onefinnet.com/signup", "_blank");
                  }}
                >
                  Try Talent for Free
                </EcnButtonFilledV2>
              </div>
            </div>
          </div>

          <Dialog
            open={uploadClicked && !isAutofillResumeParsed}
            onClose={() => { }}
            style={{
              background: "rgba(255, 255, 255, 0.25)",
              backgroundColor: "rgba(255, 255, 255, 0.25)",
              backdropFilter: "blur(1.5px)",
            }}
            BackdropProps={{
              sx: {
                backgroundColor: "rgba(255, 255, 255, 0.25)",
                backdropFilter: "blur(1.5px)",
              },
            }}
            PaperProps={{
              sx: {
                boxShadow:
                  "0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03)",
              },
            }}
          >
            <DialogContent sx={{ padding: "24px", backgroundColor: "none" }}>
              <Lottie
                options={{
                  loop: true,
                  autoplay: true,
                  animationData: ParsingLottie,
                }}
                height={300}
                width={300}
              />
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  textAlign: "center",
                  maxWidth: "100%",
                  width: "400px",
                  wordBreak: "break-word",
                }}
              >
                Please wait few seconds while Onefinnet Talent autofills your
                application
              </p>
            </DialogContent>
          </Dialog>
          <Dialog open={successView} onClose={popupCloseHandler}>
            <DialogContent sx={{ padding: "40px 120px" }}>
              <Lottie
                options={{
                  loop: true,
                  autoplay: true,
                  animationData: SuccessLottie,
                }}
                height={200}
                width={200}
              />
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  textAlign: "center",
                  marginTop: "30px",
                }}
              >
                Your application successfully submitted!
              </p>
            </DialogContent>
          </Dialog>
          {!showPromo && jobData?.show_watermark ? (
            <div
              className="floating-container"
              onClick={() => setShowPromo(true)}
            >
              <p>Created in</p>
              <img src="/logo.svg" alt="Elevate 2.0 icon" />
            </div>
          ) : (
            <Dialog
              open={showPromo}
              onClose={() => setShowPromo(false)}
              maxWidth="md"
              className="promo-dialog"
              PaperProps={{
                sx: {
                  position: "absolute",
                  bottom: "10px",
                  right: "10px",

                  m: 0,
                },
              }}
              slotProps={{
                backdrop: {
                  style: {
                    boxShadow: "none",
                    backgroundColor: "transparent",
                  },
                },
              }}
            >
              <div
                className="promo-dialog-container"
                style={{ borderRadius: radius }}
              >
                <div>
                  <img
                    src="https://ecn.blob.core.windows.net/ecn/images/talent ad.png"
                    alt=""
                    className="ad-img"
                  />
                </div>
                <div className="text-container">
                  <h1>Post a Job today</h1>
                  <p>
                    With Onefinnet Talent, you can quickly post a job and find
                    the right candidates.
                  </p>
                </div>
                <EcnButtonFilledV2
                  className="featured-button"
                  sx={{ width: "200px", borderRadius: buttonRadius }}
                  onClick={() => {
                    window.open(
                      "https://admin.onefinnet.com/signup?client=third-party&cupoun=FINFIRST",
                      "_blank"
                    );
                  }}
                >
                  Post a Featured Job
                </EcnButtonFilledV2>
                <EcnButtonFilledV2
                  className="try-button"
                  sx={{ width: "200px", borderRadius: buttonRadius }}
                  onClick={() => {
                    window.open("https://admin.onefinnet.com/signup", "_blank");
                  }}
                >
                  Try Talent for Free
                </EcnButtonFilledV2>
              </div>
            </Dialog>
          )}
        </div >
      ) : (
        <NotFound />
      )}
    </>
  );
};

export default Apply;

// const data = {
//   job_id: "12345",
//   user_id: "67890",
//   basic_details: {
//     first_name: "Aamir",
//     last_name: "Khan",
//     email: "aamir.khan@example.com",
//     phone: "123-456-7890",
//   },
//   resume: "path/to/resume.pdf",
//   academic_scores: { act: 30, gpa: 3.8, sat: 1400, gmat: 700 },
//   education: [
//     {
//       education_type: 1,
//       graduation_year: 2022,
//       name: "Example University",
//       partner_club: "Business Club",
//     },
//   ],
//   work_experiences: [
//     {
//       city: "New York",
//       company: "Tech Corp",
//       end_date: "2030-10-05T14:48:00.000Z",
//       experience_status: 1,
//       experience_type: 1,
//       industry: "Technology",
//       position: "Software Engineer",
//       start_date: "2025-01-01",
//       title: "Senior Developer",
//       zip_code: "10001",
//       logo: "path/to/logo.png",
//       description: "Developed cutting-edge software solutions.",
//     },
//   ],
//   job_questions: [
//     {
//       name: "How many years of experience do you have in Venture Capital, Investing, Start-ups or Related Fields? (Note: VC experience is not required, we just want to get a sense)",
//       type: 2,
//       key: "",
//       save: false,
//       options: [
//         "No experience",
//         "Less than 1 year",
//         "1-2 years",
//         "3-4 years",
//         "5+ years",
//       ],
//       values: ["1-2 years"],
//       required: true,
//     },
//     {
//       name: "What describes your work Authorization status",
//       type: 2,
//       key: "",
//       save: false,
//       options: [
//         "US Citizen",
//         "US Green Card",
//         "Other Work Authorization to work in US",
//         "International Citizen / STEM OPT",
//         "International Citizen / No STEM OPT Status",
//         "Other",
//       ],
//       values: ["Other Work Authorization to work in US"],
//       required: true,
//     },
//     {
//       name: "Which of the following financial software are you proficient in?",
//       type: 5,
//       key: "",
//       save: false,
//       options: ["Microsoft Excel", "CapIQ", "Pitchbook", "None"],
//       values: ["Microsoft Excel", "CapIQ", "Pitchbook"],
//       required: true,
//     },
//     {
//       name: "What year do you graduate your MBA program",
//       type: 2,
//       key: "",
//       save: false,
//       options: ["2024", "2025", "Other"],
//       values: ["2025"],
//       required: true,
//     },
//     {
//       name: "Can you work in person in NYC?",
//       type: 2,
//       key: "",
//       save: false,
//       options: ["Yes", "No"],
//       values: ["No"],
//       required: true,
//     },
//   ],
// };

const eduTemplate = {
  education_type: 0,
  graduation_year: 0,
  name: "",
  degree: "",
  start_date: "",
  end_date: "2030-10-05T14:48:00.000Z",
  department: "",
  institute: "",
  currently_studying: false,
};

const workTemplate = {
  city: "",
  company: "",
  end_date: "2030-10-05T14:48:00.000Z",
  industry: "",
  position: "",
  start_date: "",
  title: "",
  zip_code: "",
  logo: "",
  description: "",
  currently_working: false,
  summary: "",
};
